import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getProject, postDemoBuild, demoUrl } from '../lib/api';
import {
  BuildPhase,
  fetchVendoredJiraMock,
  runDemoBuild,
} from '../lib/buildDemo';
import { scaffoldFiles } from '../lib/demoScaffold';
import { Project } from '../types';

/**
 * /pipeline/:id/demo — the dedicated route for running the user's generated
 * jira-mock demo in the browser via WebContainer.
 *
 * Lifecycle:
 *   checking  HEAD-fetch the persisted demo URL on mount.
 *   idle      Persisted build absent — show "Build & Run".
 *   building  WebContainer is installing/compiling. Log pane visible.
 *   ready     Persisted build present — iframe takes the screen, Rebuild
 *             button stays in the chrome.
 *   failed    Build aborted. Show the captured log + Retry.
 */

type PageStatus = 'checking' | 'idle' | 'building' | 'ready' | 'failed';

export function PipelineDemo() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [status, setStatus] = useState<PageStatus>('checking');
  const [phase, setPhase] = useState<BuildPhase>('idle');
  const [logLines, setLogLines] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const buildingRef = useRef(false);
  const logBoxRef = useRef<HTMLPreElement | null>(null);

  // Fetch project metadata + check whether a persisted build exists.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const { project: p } = await getProject(projectId);
        if (cancelled) return;
        setProject(p);
        const url = demoUrl(projectId);
        const probe = await fetch(url, { method: 'HEAD' }).catch(() => null);
        if (cancelled) return;
        setStatus(probe && probe.ok ? 'ready' : 'idle');
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? String(e));
        setStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!logBoxRef.current) return;
    logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
  }, [logLines]);

  function appendLog(chunk: string) {
    setLogLines((prev) => {
      const next = [...prev, chunk];
      // Cap so a runaway compile log doesn't eat memory.
      return next.length > 800 ? next.slice(next.length - 800) : next;
    });
  }

  async function build() {
    if (buildingRef.current || !projectId || !project) return;
    buildingRef.current = true;
    setStatus('building');
    setError(null);
    setLogLines([]);

    try {
      if (!window.crossOriginIsolated) {
        throw new Error(
          'window.crossOriginIsolated is false — COOP/COEP headers are not in effect. ' +
            'Restart `npm start` so src/setupProxy.js is picked up, and reload this page.',
        );
      }

      appendLog('[demo] fetching vendored jira-mock …\n');
      const vendor = await fetchVendoredJiraMock();

      appendLog('[demo] starting build …\n');
      const result = await runDemoBuild(
        { project, vendor, scaffold: scaffoldFiles },
        { onLog: appendLog, onPhase: setPhase, command: 'build' },
      );

      appendLog(`[demo] uploading ${Object.keys(result.files).length} files …\n`);
      const upload = await postDemoBuild(projectId, result.files);
      appendLog(
        `[demo] uploaded ${upload.fileCount} files (${upload.bytes} bytes).\n`,
      );

      setStatus('ready');
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      appendLog(`[demo] ERROR: ${msg}\n`);
      setError(msg);
      setStatus('failed');
    } finally {
      buildingRef.current = false;
    }
  }

  if (!projectId) {
    return (
      <div style={center}>
        <div style={card}>Missing project id in URL.</div>
      </div>
    );
  }

  return (
    <div style={root}>
      <header style={topBar}>
        <button onClick={() => navigate(`/pipeline/${projectId}`)} style={backBtn}>
          ← Pipeline
        </button>
        <div style={titleBlock}>
          <span style={titleProject}>{project?.appName ?? '…'}</span>
          <span style={titleSub}>demo</span>
        </div>
        <div style={{ flex: 1 }} />
        <StatusPill status={status} phase={phase} />
        {status === 'ready' && (
          <button onClick={build} style={primaryBtn}>
            Rebuild
          </button>
        )}
        {(status === 'idle' || status === 'failed') && (
          <button onClick={build} style={primaryBtn} disabled={!project}>
            {status === 'failed' ? 'Retry build' : 'Build & Run'}
          </button>
        )}
      </header>

      <main style={main}>
        {status === 'checking' && <div style={center}>checking …</div>}

        {(status === 'building' || status === 'failed') && (
          <div style={buildPanel}>
            <pre ref={logBoxRef} style={logPane}>
              {logLines.join('')}
            </pre>
            {status === 'failed' && error && (
              <div style={errBox}>
                <strong>Build failed</strong>
                <div style={{ marginTop: 4 }}>{error}</div>
              </div>
            )}
            {status === 'building' && (
              <div style={hint}>
                First build with cold caches takes ~2 min (npm install + webpack).
                Subsequent rebuilds are faster — WebContainer keeps node_modules
                across runs while this tab is open.
              </div>
            )}
          </div>
        )}

        {status === 'idle' && (
          <div style={center}>
            <div style={card}>
              <h2 style={{ marginTop: 0 }}>No demo built yet</h2>
              <p style={{ color: '#5e6c84' }}>
                Click <strong>Build &amp; Run</strong> to compile this project's
                generated source inside a WebContainer in your browser tab and
                serve it from the orchestrator afterwards.
              </p>
              <p style={{ color: '#5e6c84', fontSize: 13 }}>
                The first build takes ~2 minutes. Once complete it stays available
                to anyone with project access — they won't need to rebuild.
              </p>
            </div>
          </div>
        )}

        {status === 'ready' && (
          <iframe
            title="demo"
            src={demoUrl(projectId)}
            style={demoFrame}
            // The persisted assemblyLine is same-origin, so we don't need a sandbox
            // restriction here — but we keep one for parity with how the SPA
            // sandboxes other html artifacts.
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        )}
      </main>
    </div>
  );
}

function StatusPill({
  status,
  phase,
}: {
  status: PageStatus;
  phase: BuildPhase;
}) {
  const label =
    status === 'checking'
      ? 'checking'
      : status === 'idle'
        ? 'not built'
        : status === 'ready'
          ? 'ready'
          : status === 'failed'
            ? 'failed'
            : phase === 'idle'
              ? 'starting'
              : phase;
  const palette =
    status === 'ready'
      ? { bg: '#e3f6e3', fg: '#1b5e20' }
      : status === 'failed'
        ? { bg: '#fdecea', fg: '#611a15' }
        : status === 'building'
          ? { bg: '#fff4d6', fg: '#7a5a00' }
          : { bg: '#eee', fg: '#555' };
  return (
    <span
      style={{
        padding: '4px 12px',
        background: palette.bg,
        color: palette.fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {label}
    </span>
  );
}

// Inline styles — kept here rather than in styles.css because PipelineDemo is
// a full-page replacement (not part of the existing pipeline shell) and the
// styles are self-contained.
const root: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100%',
  background: '#fafbfc',
  fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
};
const topBar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
  borderBottom: '1px solid #ebecf0',
  background: 'white',
};
const backBtn: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  color: '#0052cc',
  cursor: 'pointer',
  padding: 0,
  fontSize: 13,
};
const titleBlock: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 8 };
const titleProject: React.CSSProperties = { fontWeight: 600, fontSize: 15 };
const titleSub: React.CSSProperties = { color: '#5e6c84', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em' };
const primaryBtn: React.CSSProperties = {
  background: '#0052cc',
  color: 'white',
  border: 0,
  padding: '6px 14px',
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
};
const main: React.CSSProperties = { flex: 1, display: 'flex', minHeight: 0 };
const center: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
const card: React.CSSProperties = {
  background: 'white',
  border: '1px solid #ebecf0',
  borderRadius: 6,
  padding: 24,
  maxWidth: 480,
  boxShadow: '0 1px 1px rgba(9,30,66,.13)',
};
const buildPanel: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: 16,
  gap: 12,
  minHeight: 0,
};
const logPane: React.CSSProperties = {
  flex: 1,
  background: '#111',
  color: '#9fef9f',
  padding: 14,
  borderRadius: 6,
  margin: 0,
  overflow: 'auto',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
};
const hint: React.CSSProperties = { color: '#5e6c84', fontSize: 12 };
const errBox: React.CSSProperties = {
  padding: 12,
  background: '#fdecea',
  border: '1px solid #f5c6cb',
  borderRadius: 6,
  color: '#611a15',
  whiteSpace: 'pre-wrap',
};
const demoFrame: React.CSSProperties = { flex: 1, border: 0, background: 'white' };
